import { useState, useCallback, useEffect } from 'react';
import type { Project } from '@/types';
import { getProject as getProjectApi, getErrorMessage } from '@/lib/api';

interface UseProjectReturn {
  project: Project | null;
  threadCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching a single project by ID
 */
export const useProject = (projectId: string | undefined): UseProjectReturn => {
  const [project, setProject] = useState<Project | null>(null);
  const [threadCount, setThreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = useCallback(async () => {
    if (!projectId) {
      setProject(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getProjectApi(projectId);
      setProject(response.project);
      setThreadCount(response.threadCount);
    } catch (err) {
      console.error('Failed to fetch project:', err);
      setError(getErrorMessage(err));
      setProject(null);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  return {
    project,
    threadCount,
    isLoading,
    error,
    refetch: fetchProject,
  };
};

export default useProject;
